import React, { type JSX } from "react";

import { t } from "@impero-com/common/utils/i18n";
import { Alert } from "@impero-com/ui";
import Skeleton from "react-loading-skeleton";

import { ConditionalRender } from "components/conditionalRender";

import { FileList } from "./fileList";
import { FileSelector } from "./fileSelector";
import { DeleteBlobAction } from "./queries/useDeleteBlobMutation";
import { useFileExtensionWhitelistQuery } from "./queries/useFileExtensionWhitelistQuery";
import * as theme from "./theme.css";
import { UploaderFile } from "./types";

export type SetFilesCallback = (
    prevFiles: (prev: UploaderFile[]) => UploaderFile[]
) => void;
interface FileUploaderProps {
    value: UploaderFile[];
    setValue: SetFilesCallback;
    preUploadCallback?: () => Promise<boolean>;
    disabled?: boolean;
    deleteAction: DeleteBlobAction;
    emptyText?: string;
    uploaderRef?: React.RefCallback<HTMLElement>;
    hideMissing?: boolean;
    /**
     * Maximum number of files that can be uploaded. If not specified, there is no limit.
     */
    maxFiles?: number;
}
export const FileUploader = ({
    preUploadCallback,
    value,
    setValue,
    deleteAction,
    emptyText,
    uploaderRef,
    disabled = false,
    hideMissing = false,
    maxFiles = undefined,
}: FileUploaderProps): JSX.Element => {
    const { data: fileExtensionWhitelist, status: whitelistQueryStatus } =
        useFileExtensionWhitelistQuery();

    const maxFilesLimitReached = React.useMemo(() => {
        if (maxFiles === undefined) {
            return false;
        }
        return value.length >= maxFiles;
    }, [maxFiles, value]);

    // Note: we cannot use <ConditionalRender> here, because `maxFiles` can be undefined and TypeScript
    // would not be aware of `maxFiles !== undefined` check in the `rendered` prop.
    const maybeMaxFilesLimitAlert = React.useMemo(() => {
        if (disabled || !maxFilesLimitReached || maxFiles === undefined) {
            return null;
        }
        return (
            <Alert
                type="warning"
                message={t("FileUploader_MaxLimitReached_Warning", {
                    limit: maxFiles,
                })}
            />
        );
    }, [disabled, maxFiles, maxFilesLimitReached]);

    return (
        <div ref={uploaderRef} className={theme.fileUploader}>
            <ConditionalRender rendered={!disabled && !maxFilesLimitReached}>
                {whitelistQueryStatus === "success" ? (
                    <FileSelector
                        setFiles={setValue}
                        preUploadCallback={preUploadCallback}
                        fileExtensionWhitelist={fileExtensionWhitelist}
                    />
                ) : (
                    <Skeleton className={theme.dropzoneSkeleton} />
                )}
            </ConditionalRender>

            {maybeMaxFilesLimitAlert}

            <FileList
                deleteDisabled={disabled}
                hideMissing={hideMissing}
                setFiles={setValue}
                files={value}
                deleteAction={deleteAction}
                emptyText={emptyText}
                disabled={disabled}
            />
        </div>
    );
};
